import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { PROTOTYPE_AR, PROTOTYPE_EN } from "./prototype-strings";
import { OVERRIDES } from "./overrides";
import { PLATFORM_STRINGS } from "./platform-strings";
import { CORRECTION_STRINGS } from "./correction-strings";
import { OWNER_STRINGS } from "./owner-strings";
import { QUICKPAY_STRINGS } from "./quickpay-strings";
import { AGENTPKG_STRINGS } from "./agentpkg-strings";
import { CHART_STRINGS } from "./chart-strings";
import { POSTING_STRINGS } from "./posting-strings";
import { TEMPLATE_STRINGS } from "./template-strings";
import { PACKAGE_STRINGS } from "./package-strings";
import { PASS29_STRINGS } from "./pass29-strings";
import { EXPENSE_STRINGS } from "./expense-strings";
import { ITEM_SHEET_STRINGS } from "./item-sheet-strings";
import { MODULE_STRINGS } from "./module-strings";
import { IMPORT_STRINGS } from "./import-strings";
import { BUSINESS_STRINGS } from "./business-strings";
import { RESELLER_STRINGS } from "./reseller-strings";
import { ADD_BUSINESS_STRINGS } from "./add-business-strings";
import { TOBACCO_STRINGS } from "./tobacco-strings";
import { DEMO_STRINGS } from "./demo-strings";
import { DESKTOP_STRINGS } from "./desktop-strings";
import { PRINT_DIAG_STRINGS } from "./print-diag-strings";
import { OFFLINE_STRINGS } from "./offline-strings";
import { DATA_STRINGS } from "./data-strings";
import { WEBSITE_STRINGS } from "./website-strings";
import { LEGACY_HISTORY_STRINGS } from "./legacy-history-strings";

export type Lang = "en" | "ar";
const DICT: Record<Lang, Record<string, string>> = {
  en: { ...PROTOTYPE_EN, ...OVERRIDES.en, ...PLATFORM_STRINGS.en, ...CORRECTION_STRINGS.en, ...OWNER_STRINGS.en, ...QUICKPAY_STRINGS.en, ...AGENTPKG_STRINGS.en, ...CHART_STRINGS.en, ...POSTING_STRINGS.en, ...TEMPLATE_STRINGS.en, ...PASS29_STRINGS.en, ...PACKAGE_STRINGS.en, ...EXPENSE_STRINGS.en, ...MODULE_STRINGS.en, ...IMPORT_STRINGS.en, ...BUSINESS_STRINGS.en, ...RESELLER_STRINGS.en, ...TOBACCO_STRINGS.en, ...ADD_BUSINESS_STRINGS.en, ...DEMO_STRINGS.en, ...WEBSITE_STRINGS.en, ...ITEM_SHEET_STRINGS.en, ...LEGACY_HISTORY_STRINGS.en, ...DESKTOP_STRINGS.en, ...PRINT_DIAG_STRINGS.en, ...OFFLINE_STRINGS.en, ...DATA_STRINGS.en },
  ar: { ...PROTOTYPE_AR, ...OVERRIDES.ar, ...PLATFORM_STRINGS.ar, ...CORRECTION_STRINGS.ar, ...OWNER_STRINGS.ar, ...QUICKPAY_STRINGS.ar, ...AGENTPKG_STRINGS.ar, ...CHART_STRINGS.ar, ...POSTING_STRINGS.ar, ...TEMPLATE_STRINGS.ar, ...PASS29_STRINGS.ar, ...PACKAGE_STRINGS.ar, ...EXPENSE_STRINGS.ar, ...MODULE_STRINGS.ar, ...IMPORT_STRINGS.ar, ...BUSINESS_STRINGS.ar, ...RESELLER_STRINGS.ar, ...TOBACCO_STRINGS.ar, ...ADD_BUSINESS_STRINGS.ar, ...DEMO_STRINGS.ar, ...WEBSITE_STRINGS.ar, ...ITEM_SHEET_STRINGS.ar, ...LEGACY_HISTORY_STRINGS.ar, ...DESKTOP_STRINGS.ar, ...PRINT_DIAG_STRINGS.ar, ...OFFLINE_STRINGS.ar, ...DATA_STRINGS.ar }
};

/** Every string of one language, for matching text written in either language (file headings, for example). */
export const STRINGS_FOR = (l: Lang): Record<string, string> => DICT[l];

const KEY = "delta.lang";
function initialLang(): Lang {
  const q = new URLSearchParams(location.search).get("lang");
  if (q === "ar" || q === "en") {
    try { localStorage.setItem(KEY, q); } catch { /* storage blocked */ }
    return q;
  }
  try { return localStorage.getItem(KEY) === "ar" ? "ar" : "en"; } catch { return "en"; }
}

export type T = (key: string, vars?: Record<string, string | number>) => string;
interface LangCtx { lang: Lang; t: T; setLang(l: Lang): void; L(o: { nameEn: string; nameAr: string } | null | undefined): string }
const Ctx = createContext<LangCtx | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(KEY, l); } catch { /* storage blocked: keep for this page only */ }
  }, []);
  const value = useMemo<LangCtx>(() => ({
    lang,
    setLang,
    t: (key, vars) => {
      let s = DICT[lang][key] ?? DICT.en[key] ?? key;
      if (vars) for (const [a, b] of Object.entries(vars)) s = s.split(`{${a}}`).join(String(b));
      return s;
    },
    L: (o) => (o ? (lang === "ar" ? o.nameAr : o.nameEn) : "")
  }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLang(): LangCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useLang outside LangProvider");
  return c;
}
